import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AdminPage } from "./pages/AdminPage";
import { CooperationPage } from "./pages/CooperationPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DelegationsPage } from "./pages/DelegationsPage";
import { DiscussionsPage } from "./pages/DiscussionsPage";
import { GovernancePage } from "./pages/GovernancePage";
import { HelpPage } from "./pages/HelpPage";
import { LegislationPage } from "./pages/LegislationPage";
import { MemberProfilePage } from "./pages/MemberProfilePage";
import { MembersPage } from "./pages/MembersPage";
import { MyWorkspacePage } from "./pages/MyWorkspacePage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { ProposalDetailPage } from "./pages/ProposalDetailPage";
import { ProfilePage } from "./pages/ProfilePage";
import { canUsePublicSiteEditor, canViewAdmin, isObserverUser } from "./lib/domain";
import { useLexData } from "./hooks/useLexData";

export default function App({ dataMode = "demo" }: { dataMode?: "convex" | "demo" }) {
  const lexData = useLexData(dataMode);

  if (lexData.status !== "ready") {
    return <StartupScreen title={lexData.title} body={lexData.body} />;
  }

  const data = lexData.data;
  // Observers can browse network projects + legislative action, but the member-only areas
  // (directory, governance, votes, expertise exchange) redirect them back to the dashboard.
  const observer = isObserverUser(data.viewer);
  const memberOnly = (element: JSX.Element) => (observer ? <Navigate to="/discussions" replace /> : element);

  return (
    <AppShell data={data} mode={dataMode}>
      <Routes>
        <Route path="/" element={<Navigate to="/discussions" replace />} />
        <Route path="/discussions" element={<DashboardPage data={data} mode={dataMode} />} />
        <Route path="/network-projects" element={<DiscussionsPage data={data} mode={dataMode} />} />
        <Route path="/discussions/proposals/:proposalId" element={<ProposalDetailPage data={data} mode={dataMode} />} />
        <Route path="/legislation" element={<LegislationPage data={data} mode={dataMode} />} />
        <Route path="/legislative-action" element={<LegislationPage data={data} mode={dataMode} />} />
        <Route path="/members" element={memberOnly(<MembersPage data={data} mode={dataMode} />)} />
        <Route path="/members-directory" element={memberOnly(<MembersPage data={data} mode={dataMode} />)} />
        <Route path="/members/:firmId" element={memberOnly(<MemberProfilePage data={data} mode={dataMode} />)} />
        <Route path="/cooperation" element={memberOnly(<CooperationPage data={data} mode={dataMode} />)} />
        <Route path="/expertise-exchange" element={memberOnly(<CooperationPage data={data} mode={dataMode} />)} />
        <Route path="/governance" element={memberOnly(<GovernancePage data={data} mode={dataMode} />)} />
        <Route path="/my-workspace" element={memberOnly(<MyWorkspacePage data={data} mode={dataMode} />)} />
        <Route path="/my-files" element={<Navigate to="/my-workspace" replace />} />
        <Route path="/my-workspace/delegations" element={memberOnly(<DelegationsPage data={data} mode={dataMode} />)} />
        <Route path="/my-files/delegations" element={<Navigate to="/my-workspace/delegations" replace />} />
        <Route path="/profile" element={<ProfilePage data={data} mode={dataMode} />} />
        <Route path="/help" element={<HelpPage data={data} mode={dataMode} />} />
        <Route path="/notifications" element={<NotificationsPage data={data} mode={dataMode} />} />
        <Route path="/admin" element={canViewAdmin(data.viewer) ? <AdminPage data={data} view="home" mode={dataMode} /> : <Navigate to="/discussions" replace />} />
        <Route path="/admin/public-site" element={canUsePublicSiteEditor(data.viewer) ? <AdminPage data={data} view="public-site" mode={dataMode} /> : <Navigate to="/discussions" replace />} />
        <Route path="/admin/reports" element={canViewAdmin(data.viewer) ? <AdminPage data={data} view="reports" mode={dataMode} /> : <Navigate to="/discussions" replace />} />
        <Route path="*" element={<Navigate to="/discussions" replace />} />
      </Routes>
    </AppShell>
  );
}

function StartupScreen({ title, body, canReload = false }: { title: string; body: string; canReload?: boolean }) {
  return (
    <div className="grid min-h-screen place-items-center bg-app px-4 text-ink">
      <main className="w-full max-w-md rounded-xl border border-line bg-surface p-6 shadow-soft">
        <div className="mb-3 grid h-11 w-11 place-items-center rounded-lg bg-primary font-semibold text-white">L</div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-secondary">{body}</p>
        {canReload ? (
          <button type="button" className="mt-4 min-h-11 rounded-lg bg-primary px-4 text-sm font-semibold text-white" onClick={() => window.location.reload()}>
            Reload
          </button>
        ) : null}
      </main>
    </div>
  );
}
